// Faithful port of react-i18nify's `I18n._translate`/`_fetchTranslation`/
// `_replace` (vendored inside react-redux-i18n), which every `<Translate as
// T>` call site in this codebase currently depends on for key lookup,
// pluralization, and `%{var}` interpolation. ECDEX-3440 replaces *how*
// re-renders are triggered (Redux -> Context), not this resolution logic, so
// this stays byte-for-byte equivalent rather than a rewrite.
export type TranslationTree = Record<string, any>;
export type Replacements = Record<string, any>;

const handleMissingTranslation = (key: string): string => {
  const segments = key.split('.');
  return segments.at(-1) ?? key;
};

const fetchTranslation = (
  translations: any,
  key: string,
  count: number | null = null,
): any => {
  const dotIndex = key.indexOf('.');
  if (translations === undefined) {
    throw new TypeError('not found');
  }
  if (dotIndex > -1) {
    return fetchTranslation(
      translations[key.substring(0, dotIndex)],
      key.substring(dotIndex + 1),
      count,
    );
  }
  if (count !== null) {
    if (translations[`${key}_${count}`]) {
      return translations[`${key}_${count}`];
    }
    if (count !== 1 && translations[`${key}_plural`]) {
      return translations[`${key}_plural`];
    }
  }
  if (translations[key]) {
    return translations[key];
  }
  throw new Error('not found');
};

const replaceTokens = (translation: any, replacements: Replacements): any => {
  if (typeof translation === 'string') {
    let result = translation;
    Object.keys(replacements).forEach(replacement => {
      result = result
        .split(`%{${replacement}}`)
        .join(replacements[replacement]);
    });
    return result;
  }
  if (translation !== null && typeof translation === 'object') {
    const result: Record<string, any> = {};
    Object.keys(translation).forEach(translationKey => {
      result[translationKey] = replaceTokens(
        translation[translationKey],
        replacements,
      );
    });
    return result;
  }
  return null;
};

// `count` is passed through as-is (not defaulted here) so that omitting it
// from `replacements` reproduces react-i18nify's `count = null` default
// parameter behavior in `fetchTranslation` above.
export const translate = (
  translations: TranslationTree,
  locale: string,
  key: string,
  replacements: Replacements = {},
): any => {
  try {
    const translationLocale = translations[locale]
      ? locale
      : locale.split('-')[0];
    const translation = fetchTranslation(
      translations,
      `${translationLocale}.${key}`,
      replacements.count,
    );
    return replaceTokens(translation, replacements);
  } catch {
    return handleMissingTranslation(key);
  }
};
